import React from 'react';
import { View, Text, Platform } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { auditHistoryHeaderStyles as styles } from '../styles/auditHistoryHeader.styles';
import { formatHeaderDate, formatStatusDisplay } from '../utils/auditHistoryUtils';
import { emailToDisplayName } from '../utils/formOptions';
import { useTheme } from '../../../theme';

interface AuditHistoryHeaderProps {
  status?: string;
  createdDate?: string;
  createdBy?: string;
  targetDate?: string;
  origin?: string; // When provided, displays origin information (e.g., "Stand-up")
}

const AuditHistoryHeader: React.FC<AuditHistoryHeaderProps> = ({
  status = 'Pending',
  createdDate,
  createdBy,
  targetDate,
  origin,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  // Format the createdBy field to show only first and last name
  const formatCreatedBy = (value?: string): string => {
    if (!value) return '';
  
    if (value.includes('@')) {
      return emailToDisplayName(value);
    }
   
    if (value.includes(',')) {
      const parts = value.split(',').map(p => p.trim());
      if (parts.length === 2) {
        return `${parts[1]} ${parts[0]}`;
      }
    }
    return value;
  };

  const displayCreatedBy = formatCreatedBy(createdBy);

  // Determine badge styling based on status
  const getStatusBadgeStyle = () => {
    if (!status) return styles.statusBadge;
    
    const statusLower = status.toLowerCase();
    
    // Check for declined
    if (statusLower === 'declined') {
      return styles.statusBadgeDeclined;
    }
    
    // Check for overdue
    if (statusLower === 'overdue') {
      return styles.statusBadgeOverdue;
    }
    
    // Check for in progress - handle various formats
    if (statusLower.includes('in progress') || statusLower === 'in_progress' || statusLower === 'inprogress') {
      return styles.statusBadgeInProgress;
    }
    
    // Default to statusBadge (completed/green)
    return styles.statusBadge;
  };

  const shouldShowIcon = () => {
    const statusLower = status?.toLowerCase() || '';
    return statusLower !== 'declined';
  };

  return (
    <View style={[styles.container, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 }]}>
      <View style={styles.header}>
        <Text style={[styles.title, isDark && { color: colors.text.primary }]}>Action plan audit trail</Text>
      </View>

      {origin && (
        <View style={styles.statusSection}>
          <Text style={[styles.label, isDark && { color: colors.text.secondary }]}>Origin:</Text>
          <View style={[styles.badge, isDark && { backgroundColor: colors.background.elevated }]}>
            <Text style={[styles.badgeText, { color: isDark ? colors.text.primary : '#2D2D2D' }]}>
              {origin}
            </Text>
          </View>
        </View>
      )}

      <View style={styles.statusSection}>
        <Text style={[styles.label, isDark && { color: colors.text.secondary }]}>Status:</Text>
        <View style={[styles.badge, getStatusBadgeStyle()]}>
          {/* {shouldShowIcon() && (
            <MaterialIcons name="check-circle" size={16} color="#FFFFFF" />
          )} */}
          <Text style={[styles.badgeText, !shouldShowIcon() && styles.badgeTextDeclined]}>
            {formatStatusDisplay(status)}
          </Text>
        </View>
      </View>

      <View style={styles.createdSection}>
        <Text style={[styles.infoText, isDark && { color: colors.text.secondary }]}>
          Created on <Text style={[styles.bold, isDark && { color: colors.text.primary }]}>{formatHeaderDate(createdDate)}</Text>
          {displayCreatedBy && (
            <>
              {' '}by <Text style={[styles.bold, isDark && { color: colors.text.primary }]}>{displayCreatedBy}</Text>
            </>
          )}
        </Text>
      </View>

      {targetDate && (
        <View style={styles.targetDateSection}>
          <View style={[styles.iconCircleContainer, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
            <MaterialIcons name="calendar-today" size={16} color={isDark ? colors.brand.primary : '#034895'} />
          </View>
          <Text style={[styles.infoText, isDark && { color: colors.text.secondary }]}>
            Target date: <Text style={[styles.bold, isDark && { color: colors.text.primary }]}>{formatHeaderDate(targetDate)}</Text>
          </Text>
        </View>
      )}
    </View>
  );
};

export default AuditHistoryHeader;

